import { pauseWordBoundary } from "./inlinePauses";

export const EDITOR_DRAG_TYPE = "application/x-tableread-editor";
import { POINT_VOCAL_CUES } from './inlineVocalCues';
export type EditorInsertKind = "pause" | "scene-heading" | "action" | "dialogue" | "parenthetical" | "transition" | `cue:${string}`;
export const isPointInsert = (kind: EditorInsertKind) => kind === 'pause' || kind.startsWith('cue:');
export function readEditorDrag(transfer: DataTransfer): EditorInsertKind | null {
  const kind = transfer.getData(EDITOR_DRAG_TYPE);
  return ["pause", "scene-heading", "action", "dialogue", "parenthetical", "transition"].includes(kind) ||
    kind.startsWith('cue:') && (POINT_VOCAL_CUES as readonly string[]).includes(kind.slice(4)) ? kind as EditorInsertKind : null;
}

/** A transparent, identically styled mirror lets browser text layout resolve a
 * drop to a real character offset, including wrapped lines and zoom. */
export function textareaDropCaret(input: HTMLTextAreaElement, x: number, y: number) {
  const box = input.getBoundingClientRect(), css = getComputedStyle(input);
  const mirror = document.createElement("div");
  for (const property of ["font-family", "font-size", "font-weight", "font-style", "line-height", "letter-spacing", "word-spacing",
    "text-align", "text-transform", "text-indent", "padding", "border-width", "box-sizing", "tab-size", "overflow-wrap", "word-break"]) {
    mirror.style.setProperty(property, css.getPropertyValue(property));
  }
  Object.assign(mirror.style, { position: "fixed", left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`,
    height: `${box.height}px`, whiteSpace: "pre-wrap", borderStyle: "solid", borderColor: "transparent", overflow: "hidden",
    opacity: "0", zIndex: "2147483647", pointerEvents: "auto" });
  const text = document.createTextNode(input.value || "\u200b"); mirror.append(text); document.body.append(mirror);
  mirror.scrollTop = input.scrollTop; mirror.scrollLeft = input.scrollLeft;
  try {
    const doc = document as Document & {
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
    };
    const point = doc.caretPositionFromPoint?.(x, y);
    const range = point ? document.createRange() : doc.caretRangeFromPoint?.(x, y);
    if (point && range) range.setStart(point.offsetNode, point.offset);
    if (!range || !mirror.contains(range.startContainer)) return null;
    const prefix = document.createRange(); prefix.selectNodeContents(mirror); prefix.setEnd(range.startContainer, range.startOffset);
    const offset = pauseWordBoundary(input.value, Math.min(input.value.length, prefix.toString().length));
    const caret = document.createRange(); caret.setStart(text, offset); caret.collapse(true);
    const rect = caret.getBoundingClientRect();
    return { offset, x: rect.width || rect.height ? rect.left : x, y: rect.height ? rect.top : y,
      height: rect.height || parseFloat(css.lineHeight) || 20 };
  } finally { mirror.remove(); }
}
